import debounce from 'lodash/debounce';
import React from 'react';
import { styled } from 'styled-components';
import { theme } from '../theme';
import { typography } from '../typography';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  debounceInMilliseconds?: number;
  caption: string;
}

export const ThemeInput = React.forwardRef<HTMLInputElement, InputProps & { className?: string }>((props, ref) => {
  const { debounceInMilliseconds = 300, onChange, value, caption, className, ...rest } = props;

  const [tempValue, setTempValue] = React.useState(value || '');

  // Debounce onChange handler to update parent state
  const debouncedOnChange = React.useMemo(
    () =>
      debounce((newValue: string) => {
        onChange?.({
          target: { value: newValue } as EventTarget & HTMLInputElement,
        } as React.ChangeEvent<HTMLInputElement>);
      }, debounceInMilliseconds),
    [onChange, debounceInMilliseconds],
  );

  // Update local state immediately and debounce the parent update
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTempValue(e.target.value); // Update internal state immediately
    debouncedOnChange(e.target.value); // Debounce parent update
  };

  // Keep internal state in sync when value changes externally
  React.useEffect(() => {
    setTempValue(value || '');
  }, [value]);

  // Cleanup debounce on unmount
  React.useEffect(() => {
    return () => {
      debouncedOnChange.cancel();
    };
  }, [debouncedOnChange]);

  return (
    <InputContainerStyle className={className}>
      {caption}
      <InputStyle {...rest} value={tempValue} onChange={handleChange} ref={ref} />
    </InputContainerStyle>
  );
});

const InputContainerStyle = styled.div`
  position: relative;
  width: 100%;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  ${typography('body2')};
`;

const InputStyle = styled.input`
  width: 100%;
  ${typography('body1')};
  padding: ${theme.spacing.l};
  border-radius: 990px;
  box-sizing: border-box;
  border: 2px solid ${theme.colors.input.border.default};
  width: 100%;

  &:focus {
    outline: none;
    border-color: ${theme.colors.input.border.focus};
    box-shadow: 4px 4px 10px 0px rgba(20, 20, 43, 0.06);
  }

  &::placeholder {
    color: ${theme.colors.text.hint};
  }
`;
